import { useCallback } from 'react';
import { mapApiResourceToAssistantUiAction } from '@/api/assistant/assistantMapping';
import { runVoiceToolCall } from '@/api/assistantVoice/assistantVoiceApi';
import {
  isSessionNoteUiAction,
  mapApiResourceToSessionNoteDraft,
  type SessionNoteUiActionApiResource,
} from '@/api/assistantVoice/assistantVoiceApi.types';
import {
  VOICE_FAILED_TOOL_OUTPUT,
  VOICE_NOTE_FOR_OTHER_PATIENT_OUTPUT,
  VOICE_NOTE_SAVE_FAILED_OUTPUT,
  VOICE_NOTE_SAVED_OUTPUT,
  VOICE_NO_OPEN_NOTE_OUTPUT,
} from '@/constants/assistantVoice';
import type { SessionNoteFormRegistry } from '../sessionNote/sessionNoteFormRegistry';
import { useAssistantUi } from '../uiActions/assistantUiContext';
import { useAssistantUiActionExecutor } from '../uiActions/useAssistantUiActionExecutor';
import type { RealtimeFunctionCall } from './realtimeEvents';
import { patientScreenForVoiceLookup } from './patientScreenForVoiceLookup';
import { retryOnceAfterTransientError } from './retryOnceAfterTransientError';

export type VoiceNoteEvent =
  'note_filled' | 'note_saved' | 'note_not_open' | 'note_save_failed' | 'reply_failed';

export interface VoiceToolRun {
  output: string;
  noteEvent: VoiceNoteEvent | null;
  isRecordedSilently: boolean;
}

const KEEP_LISTENING_TOOL_NAME = 'keep_listening';

const VOICE_SCREEN_REQUEST = { isRequestedByUser: true, allowLeavingUnsavedWork: false };

const OFFERED_ACTION_FIELDS = ['note', 'status'];

function withScreenOutcome(output: string, outcome: string): string {
  try {
    const parsed: unknown = JSON.parse(output);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      const kept = Object.entries(parsed).filter(function keepRealFields(entry) {
        return !OFFERED_ACTION_FIELDS.includes(entry[0]);
      });
      return JSON.stringify({
        ...Object.fromEntries(kept),
        screen: outcome,
        screen_opened: outcome === 'performed',
      });
    }
  } catch {
    return output;
  }
  return output;
}

async function applySessionNoteAction(
  forms: SessionNoteFormRegistry,
  action: SessionNoteUiActionApiResource,
  serverOutput: string
): Promise<VoiceToolRun> {
  const form = forms.current();
  if (form === null) {
    return {
      output: VOICE_NO_OPEN_NOTE_OUTPUT,
      noteEvent: 'note_not_open',
      isRecordedSilently: false,
    };
  }
  if (form.patientId !== action.patient_id) {
    return {
      output: VOICE_NOTE_FOR_OTHER_PATIENT_OUTPUT,
      noteEvent: 'note_not_open',
      isRecordedSilently: false,
    };
  }
  if (action.operation === 'save_draft') {
    const isSaved = await form.saveAsDraft();
    return isSaved
      ? { output: VOICE_NOTE_SAVED_OUTPUT, noteEvent: 'note_saved', isRecordedSilently: false }
      : {
          output: VOICE_NOTE_SAVE_FAILED_OUTPUT,
          noteEvent: 'note_save_failed',
          isRecordedSilently: false,
        };
  }
  if (action.draft !== undefined) {
    form.applyDraft(mapApiResourceToSessionNoteDraft(action.draft));
  }
  const needsReview = action.needs_review === undefined ? [] : action.needs_review;
  return {
    output: serverOutput,
    noteEvent: 'note_filled',
    isRecordedSilently: needsReview.length === 0,
  };
}

export function useVoiceToolRunner(): (call: RealtimeFunctionCall) => Promise<VoiceToolRun> {
  const { sessionNoteForms } = useAssistantUi();
  const executeAction = useAssistantUiActionExecutor();

  return useCallback(
    async function runCall(call: RealtimeFunctionCall): Promise<VoiceToolRun> {
      let result;
      try {
        result = await retryOnceAfterTransientError(function runOnServer() {
          return runVoiceToolCall(call.name, call.argumentsJson);
        });
      } catch {
        return { output: VOICE_FAILED_TOOL_OUTPUT, noteEvent: null, isRecordedSilently: false };
      }
      const uiAction = result.uiAction;
      if (call.name === KEEP_LISTENING_TOOL_NAME) {
        return { output: result.output, noteEvent: null, isRecordedSilently: true };
      }
      if (uiAction === null) {
        const patientScreen = patientScreenForVoiceLookup(call, result.output);
        if (patientScreen !== null && sessionNoteForms.current() === null) {
          await executeAction(patientScreen, VOICE_SCREEN_REQUEST);
        }
        return { output: result.output, noteEvent: null, isRecordedSilently: false };
      }
      if (isSessionNoteUiAction(uiAction)) {
        return applySessionNoteAction(sessionNoteForms, uiAction, result.output);
      }
      const outcome = await executeAction(
        mapApiResourceToAssistantUiAction(uiAction),
        VOICE_SCREEN_REQUEST
      );
      return {
        output: withScreenOutcome(result.output, outcome),
        noteEvent: null,
        isRecordedSilently: false,
      };
    },
    [executeAction, sessionNoteForms]
  );
}
